import { useEffect, useRef, useState } from 'react'
import { styled } from '@material-ui/core/styles'
import { useTranslate, useNotify } from 'react-admin'
import InvalidFile from '../exceptions/invalid_file'
import { CustomFileDetailTable } from './file_upload'

const InternalInput = styled('input')({
  display: 'none',
})

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20 Mo

const CustomFileInput = ({
  multiple,
  label = multiple
    ? 'ra.input.file.upload_several'
    : 'ra.input.file.upload_single',
  name,
  inputRef,
  defaultValue,
  onChange,
  height,
  allowDownload = false,
  onlyIcon = false,
  icon,
  disabled,
  dragNDropProps,
  acceptOnly,
}) => {
  const notify = useNotify()
  const translate = useTranslate()
  const [files, setFiles] = useState([])
  const internalInputRef = useRef()
  useEffect(() => {
    if (defaultValue) {
      if (!Array.isArray(defaultValue)) defaultValue = [defaultValue]
      for (const value of defaultValue) {
        if (typeof value === 'string') {
          setFiles([
            {
              src: value,
            },
            ...files,
          ])
        } else if (value instanceof Promise) {
          value.then((file) => {
            height = '100px'
            setFiles([file, ...files])
          })
        } else if (
          !value.src ||
          !value.name ||
          !value.rawFile ||
          !(value.rawFile instanceof File)
        ) {
          throw new InvalidFile(typeof value)
        } else setFiles([value, ...files])
      }
    }
  }, [])
  if (inputRef) inputRef(internalInputRef)

  // Make sure that it has a unique name just to identify it in the future is we want for example change the style.
  if (!name) {
    let i = 0
    name = `custom-file-${i}`
    while (document.getElementsByName(name).length > 0) {
      i++
      name = `custom-file-${i}`
    }
  }

  const CustomFileContainer = styled('div')({
    textAlign: 'center',
    margin: '0.5rem',
    '&>svg': {
      fontSize: 35,
      color: '#143A5F',
    },
  })

  const DragAndDropZone = styled('p')({
    height: height || 'auto',
    textAlign: 'center',
    verticalAlign: 'middle',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    margin: 0,
    padding: '1rem',
    color: '#bdbdbd',
    backgroundColor: '#f5f5f5',
    marginBottom: '30px',
    border: '1.5px dashed #e0e0e0',
    borderRadius: '5px',
    flexDirection: 'column',
  })
  // THE FOLLOWING FUNCTIONS HAVE BEEN TAKEN FROM REACT ADMIN IN ORDER TO RESPECT THE STRUCTURE EXPECTED BY THE ENDPOINT
  // @todo: Review

  // turn a browser dropped file structure into expected structure
  const transformFile = (file) => {
    if (!(file instanceof File)) {
      return file
    }

    return {
      rawFile: file,
      name: file.name,
      src: URL.createObjectURL(file),
    }
  }

  const transformFiles = (files) => {
    if (!files) {
      return multiple ? [] : null
    }

    if (files.some((file) => file.size > MAX_FILE_SIZE)) {
      notify(
        'Erreur : Au moins un fichier est trop lourd (> 20 Mo). Réduisez la taille du fichier et réessayez.',
        'error'
      )
      return null
    }

    if (Array.isArray(files)) {
      const tmpFiles = files.map(transformFile)
      setFiles([...tmpFiles])

      return tmpFiles
    }

    const tmpFile = transformFile(files)
    setFiles((prev) => [...prev, tmpFile])

    return tmpFile
  }
  // END OF REACT ADMIN FUNCTIONALITY

  const stopUnwantedEvents = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }
  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onChange(transformFiles([...e.dataTransfer.files]), e)
  }
  const handleClick = (e) => {
    internalInputRef.current.click()
  }

  return (
    <>
      <InternalInput
        multiple={multiple}
        ref={internalInputRef}
        name={`input-${name}`}
        accept={acceptOnly}
        type="file"
        onChange={(e) => {
          onChange(transformFiles([...e.target.files]), e)
        }}
      />
      <CustomFileContainer name={`container-${name}`}>
        {!disabled && (
          <DragAndDropZone
            onDrop={(e) => handleDrop(e)}
            onDragOver={(e) => stopUnwantedEvents(e)}
            onDragEnter={(e) => stopUnwantedEvents(e)}
            onDragLeave={(e) => stopUnwantedEvents(e)}
            onClick={handleClick}
            {...dragNDropProps}
          >
            {icon && <CustomFileContainer>{icon}</CustomFileContainer>}
            {!onlyIcon && <p>{translate(label)}</p>}
          </DragAndDropZone>
        )}
        {allowDownload && files.length > 0 && (
          <CustomFileContainer>
            <CustomFileDetailTable
              rows={files.map((file) => ({
                purpose: file.name || translate('hs.file.download'),
                url: file.src,
              }))}
            />
          </CustomFileContainer>
        )}
      </CustomFileContainer>
    </>
  )
}

export default CustomFileInput
